import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { TipsterCard } from "@/components/TipsterCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Search, Filter, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Tipsters = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tipsters, setTipsters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSport, setSelectedSport] = useState("all");
  const [sortBy, setSortBy] = useState("rating");

  const sports = ["Football", "Basketball", "Tennis", "Baseball", "Hockey", "Cricket"];

  useEffect(() => {
    fetchTipsters();
  }, [selectedSport, sortBy]);

  const fetchTipsters = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams({
        sortBy,
        limit: '50'
      });
      
      if (selectedSport !== "all") {
        params.append('sport', selectedSport);
      }
      
      const { data, error } = await supabase.functions.invoke(
        `get-tipsters?${params.toString()}`,
        { method: 'GET' }
      );

      if (error) throw error;
      
      // Transform data to match TipsterCard expectations
      const transformedTipsters = data.tipsters?.map((t: any) => ({
        id: t.id,
        name: t.profiles.display_name,
        avatar: t.profiles.avatar_url,
        country: t.profiles.country_code || "Unknown",
        roi: t.roi_percentage,
        winRate: t.win_rate,
        streak: t.current_streak,
        totalTips: t.total_tips,
        rating: t.rating,
        sports: t.sports_tags || [],
        isPremium: t.is_premium
      })) || [];

      setTipsters(transformedTipsters);
    } catch (error) {
      console.error("Error fetching tipsters:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTipsters = tipsters.filter((tipster: any) =>
    tipster.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipster.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Verified Tipsters</h1>
            <p className="text-muted-foreground">Connect with professional tipsters worldwide</p>
          </div>
          
          {user && (
            <Button onClick={() => navigate('/become-tipster')} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Become a Tipster
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tipsters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={selectedSport} onValueChange={setSelectedSport}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Sports" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="roi">ROI</SelectItem>
              <SelectItem value="winrate">Win Rate</SelectItem>
              <SelectItem value="tips">Total Tips</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="outline">{filteredTipsters.length} tipsters found</Badge>
          {selectedSport && <Badge variant="secondary">{selectedSport}</Badge>}
        </div>

        {/* Tipsters Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-card rounded-lg h-64"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTipsters.map((tipster: any) => (
              <TipsterCard key={tipster.id} tipster={tipster} />
            ))}
          </div>
        )}

        {filteredTipsters.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">No tipsters found matching your criteria</div>
            <Button variant="outline" onClick={() => {
              setSearchTerm("");
              setSelectedSport("all");
            }}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tipsters;